import React, { useEffect, useState } from 'react';
import { Bot, ChevronRight, EyeOff } from 'lucide-react';
import HelpBot from './HelpBot';

const HIDDEN_KEY = 'mm-helpbot-hidden';

/**
 * Floating launcher that opens the HelpBot drawer.
 *  - Bottom-right pulsing bot button when closed.
 *  - Click opens the drawer; drawer's own X closes it (launcher stays visible).
 *  - Small EyeOff button hides the launcher entirely; a tiny tab on the right
 *    edge restores it. Hidden state persists in localStorage.
 */
export default function FloatingHelpBot() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(HIDDEN_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(HIDDEN_KEY, hidden ? '1' : '0'); } catch {}
  }, [hidden]);

  // Ctrl+. opens the drawer from anywhere
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey && e.key === '.') {
        e.preventDefault();
        setHidden(false);
        setOpen(true);
      }
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (hidden && !open) {
    return (
      <button
        onClick={() => setHidden(false)}
        title="Show help bot"
        className="fixed right-0 bottom-24 z-40 bg-purple-600 hover:bg-purple-700 text-white p-1.5 rounded-l-md shadow-md"
        aria-label="Show help bot"
      >
        <ChevronRight className="w-3.5 h-3.5 rotate-180" />
      </button>
    );
  }

  return (
    <>
      {!open && (
        <div className="fixed right-4 bottom-4 z-40 flex flex-col items-end gap-1">
          <button
            onClick={() => setHidden(true)}
            title="Hide help bot"
            className="opacity-0 hover:opacity-100 group-hover:opacity-100 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-700 p-1 rounded-full shadow-sm"
            aria-label="Hide help bot"
          >
            <EyeOff className="w-3 h-3" />
          </button>
          <button
            onClick={() => setOpen(true)}
            title="Ask the help bot"
            className="relative group bg-ink-950 dark:bg-ink-800 text-gold-400 border border-gold-500/40 p-3.5 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all"
            aria-label="Open help bot"
          >
            <Bot className="w-6 h-6" />
          </button>
        </div>
      )}

      {open && <HelpBot onClose={() => setOpen(false)} />}
    </>
  );
}
